import { angleOverridesQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useAppSelector } from "store/store";
import { selectScrapViewportRange } from "store/slices/scrapTrackingViewportSlice";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { useGetTrainableConfigQuery } from "src/store/api/generatedApi";
import type {
  TrainableAngleConfig,
  TrainableWindow,
} from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { useTrainablePeriodsData } from "../trainable/hooks/useTrainablePeriodsData";
import { TrainablePeriodsTimeline } from "../trainable/TrainablePeriodsTimeline";
import { PERIOD_DURATION_SECONDS } from "../components/timelineChartUtils";
import { SummaryChip } from "./SummaryChip";

type TrainableSectionProps = Readonly<{
  selectedLocation: string;
  onEnabledAnglesChange?: (angleNames: string[]) => void;
  children?: React.ReactNode;
}>;

export const TrainableSection = ({
  selectedLocation,
  onEnabledAnglesChange,
  children,
}: TrainableSectionProps) => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const viewportRange = useAppSelector(selectScrapViewportRange);

  const start = viewportRange.start;
  const end = viewportRange.end;

  const {
    data: trainableConfig,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useGetTrainableConfigQuery({ tenantName: tenant });

  const allAngles = useMemo(
    () => trainableConfig?.angles ?? [],
    [trainableConfig]
  );

  const locationAngles = useMemo(
    () => allAngles.filter((a) => a.location === selectedLocation),
    [allAngles, selectedLocation]
  );

  const [angleOverrides, setAngleOverrides] = useNavigationQuery(
    "coverageAngles",
    {},
    angleOverridesQuery,
    "replace"
  );

  const angleStates: readonly TrainableAngleConfig[] = useMemo(
    () =>
      locationAngles.map((a) => {
        const key = `${a.location}/${a.angle}`;
        return {
          ...a,
          enabled: angleOverrides[key] ?? a.training_required,
        };
      }),
    [locationAngles, angleOverrides]
  );

  const enabledAngleNames = useMemo(
    () => angleStates.filter((a) => a.enabled).map((a) => a.angle),
    [angleStates]
  );

  useEffect(() => {
    onEnabledAnglesChange?.(enabledAngleNames);
  }, [enabledAngleNames, onEnabledAnglesChange]);

  const handleAngleToggle = useCallback(
    (index: number) => {
      const angle = locationAngles[index];
      if (!angle) return;
      const key = `${angle.location}/${angle.angle}`;
      setAngleOverrides((previous) => ({
        ...previous,
        [key]: !(previous[key] ?? angle.training_required),
      }));
    },
    [locationAngles, setAngleOverrides]
  );

  const { windows, isLoading: isLoadingData } = useTrainablePeriodsData(
    start,
    end,
    selectedLocation,
    angleStates,
    PERIOD_DURATION_SECONDS
  );

  const summary = useMemo(() => {
    const total = windows.length;
    const trainable = windows.filter(
      (w) =>
        w.review_coverage_ratio >= 1.0 && w.min_embedding_coverage_ratio >= 1.0
    ).length;
    const reviewed = windows.filter(
      (w) => w.review_coverage_ratio >= 1.0
    ).length;
    const embedded = windows.filter(
      (w) => w.min_embedding_coverage_ratio >= 1.0
    ).length;
    return { total, trainable, reviewed, embedded };
  }, [windows]);

  const handleWindowClick = useCallback(
    (trainableWindow: TrainableWindow) => {
      if (!selectedLocation) return;
      const windowStart = dayjs.utc(trainableWindow.window_start);
      const date = windowStart.format("YYYY-MM-DD");
      const time = windowStart.toISOString();
      const params = new URLSearchParams({ date, time });
      const url = `/${tenant}/tool/view-labels/${selectedLocation}?${params}`;
      window.open(url, "_blank");
    },
    [tenant, selectedLocation]
  );

  if (isLoadingConfig) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isConfigError) {
    return (
      <Stack p={2}>
        <Alert severity="error">{t("trainablePeriodsConfigError")}</Alert>
      </Stack>
    );
  }

  const enabledCount = angleStates.filter((a) => a.enabled).length;

  return (
    <Stack
      spacing={2}
      sx={{
        p: 3,
        backgroundColor: "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "grey.300",
      }}
    >
      <Typography variant="h5" fontWeight={600} color="text.primary">
        {t("trainablePeriodsTitle")}
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {angleStates.map((angle, index) => (
          <FormControlLabel
            key={`${angle.location}-${angle.angle}`}
            control={
              <Checkbox
                checked={angle.enabled}
                onChange={() => {
                  handleAngleToggle(index);
                }}
                size="small"
              />
            }
            label={
              angle.training_required
                ? `${angle.angle} (training)`
                : angle.angle
            }
          />
        ))}
      </Stack>

      {enabledCount === 0 ? (
        <Alert severity="info">{t("trainablePeriodsSelectAngles")}</Alert>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={4}>
            <SummaryChip
              label={t("trainablePeriodsTotal")}
              value={summary.total}
            />
            <SummaryChip
              label={t("trainablePeriodsTrainable")}
              value={summary.trainable}
              color="success.main"
            />
            <SummaryChip
              label={t("trainablePeriodsReviewed")}
              value={summary.reviewed}
            />
            <SummaryChip
              label={t("trainablePeriodsEmbedded")}
              value={summary.embedded}
            />
            {summary.total > 0 ? (
              <SummaryChip
                label={t("trainablePeriodsPercent")}
                value={`${formatNumber(1)((summary.trainable / summary.total) * 100)}%`}
                color="success.main"
              />
            ) : null}
          </Stack>

          {isLoadingData ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={24} />
            </Stack>
          ) : windows.length === 0 ? (
            <Alert severity="info">{t("trainablePeriodsNoData")}</Alert>
          ) : (
            <TrainablePeriodsTimeline
              windows={windows}
              start={start}
              end={end}
              onWindowClick={handleWindowClick}
            />
          )}
        </Stack>
      )}

      {children}
    </Stack>
  );
};
